import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Checkbox,
	defaultRichTextEditorFeatures,
	Dialog,
	Form,
	FormItem,
	imageTool,
	RichTextEditor,
	Select,
	SelectItem,
	Toolbar,
	ValidationError,
	useRichTextEditorIntermediateState,
	RichTextEditorIntermediateStateHandlers
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { SignatureKind } from "client/Api/Email/common";
import { SignatureModel } from "client/Api/Email/email";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	AvailableSignature,
	ConfigurePersonalSignatureModel,
	EditPersonalSignatureViewModel,
	getViewModel,
	save,
	SignatureConstraints
} from "client/Api/Email/Signatures/Personal/personalSignatureSettings";
import { useSignatureStore } from "../signatureStore";

const validationConfiguration: IValidationConfiguration<ConfigurePersonalSignatureModel> =
	createValidationConfiguration<ConfigurePersonalSignatureModel>();

validationConfiguration
	.forObject("personalSignature")
	.when(model => model.isEnabled)
	.forScalar("content")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type SignatureSettingsDialogProps = {
	onClose(): void;
};

export const SignatureSettingsDialog = (props: SignatureSettingsDialogProps) => {
	const {
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<ConfigurePersonalSignatureModel>({
		defaultSignature: SignatureKind.None,
		personalSignature: {
			content: "",
			isEnabled: false
		}
	});
	const [state, setState] = useState<SignatureSettingsState>({
		availableSignatures: [],
		hasEnforcedManagedSignature: false,
		signatureConstraints: undefined
	});

	const addOrUpdateSignature: (signature: SignatureModel) => void = useSignatureStore(x => x.addOrUpdate);
	const removeSignature: (signatureKind: SignatureKind) => void = useSignatureStore(x => x.remove);
	const setDefaultSignature: (signatureKind: SignatureKind) => void = useSignatureStore(x => x.setDefault);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<ConfigurePersonalSignatureModel> =
		useFormHandler(
			formData,
			setFormData,
			validationConfiguration
		);

	const richTextEditorIntermediateState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

	const {
		availableSignatures,
		hasEnforcedManagedSignature,
		signatureConstraints
	} = state;

	const {
		isEnabled
	} = formData.personalSignature;

	const availableSignaturesOptions: readonly SelectItem<SignatureKind>[] = useMemo(
		() => {
			const options: readonly AvailableSignature[] = isEnabled
					? availableSignatures
					: availableSignatures.filter(
						(x) => x.kind !== SignatureKind.Personal
					);

			return options.map(x => {
				return {
					text: x.name,
					value: x.kind
				};
			});
		},
		[
			availableSignatures,
			isEnabled
		]
	);

	const saveHandler = async (): Promise<void> => {
		try {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			setIsBusy(true);

			const model: ConfigurePersonalSignatureModel = prepareForSaving(
				formData,
				hasEnforcedManagedSignature
			);

			await save(
				model,
				globalCancellation
			);

			if (isEnabled) {
				addOrUpdateSignature({
					kind: SignatureKind.Personal,
					name: localize("_PersonalSignature"),
					content: model.personalSignature
						.content
						?? "",
					isDefault: model.defaultSignature === SignatureKind.Personal
				});
			} else {
				removeSignature(
					SignatureKind.Personal
				);
			}

			if (!hasEnforcedManagedSignature) {
				setDefaultSignature(
					model.defaultSignature
				);
			}

			setIsBusy(false);
			onClose();
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				console.error(e);
				alertManager.showError(localize("_ChangesNotSaved"));
			}

			setIsBusy(false);
		}
	};

	const changePersonalSignatureHandler = useCallback(
		(isEnabled: boolean): void => {
			if (
				!isEnabled
					&& formHandler.for("defaultSignature")
						.value === SignatureKind.Personal
			) {
				formHandler.for("defaultSignature")
					.reducer
					.setValue(SignatureKind.None);
				formHandler.for("defaultSignature")
					.validation
					.triggerInputFinished();
			}
		},
		[formHandler]
	);

	useEffect(
		() => {
			async function load(token: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const result: EditPersonalSignatureViewModel = await getViewModel(token);
					const {
						availableSignatures,
						defaultSignatureKind,
						hasEnforcedManagedSignature,
						personalSignature: {
							content,
							isEnabled
						},
						signatureConstraints
					} = result;

					setFormData({
						defaultSignature: defaultSignatureKind,
						personalSignature: {
							content: content ?? "",
							isEnabled
						}
					});

					setState({
						availableSignatures,
						hasEnforcedManagedSignature,
						signatureConstraints
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					alertManager.showError(localize("_GeneralErrorMessage"));

					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			load(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	return (
		<Dialog
			trackingId="email-signature-settings-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_SignatureSettings")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					{!hasEnforcedManagedSignature && (
						<FormItem label={localize("_ChooseDefaultSignature")}>
							<Select
								{...formHandler
									.for("defaultSignature")
									.attachToSelect()}
								options={availableSignaturesOptions}
								menuTitle={localize("_ChooseDefaultSignature")}
							/>
							<ValidationError
								{...formHandler
									.for("defaultSignature")
									.attachToValidationError()}
							/>
						</FormItem>
					)}
					<FormItem>
						<Checkbox
							{...formHandler.for("personalSignature")
								.for("isEnabled")
								.attachToCheckbox()
								.withOnChange(changePersonalSignatureHandler)}
							text={localize("_EnablePersonalSignature")}

						/>
					</FormItem>
					{isEnabled && (
						<FormItem label={localize("_PersonalSignature")}>
							<RichTextEditor
								{...formHandler
									.for("personalSignature")
									.for("content")
									.attachToRichTextEditor(richTextEditorIntermediateState)}
								supportedFeatures={[
									...defaultRichTextEditorFeatures,
									imageTool({
										imageConstraints: signatureConstraints,
										errorMessage: localize("_UploadSignatureImageError")
									})
								]}
								elementModifiers={{
									paragraph: {
										hasNoMargin: true,
									},
									image: {
										verticalAlignment: "bottom"
									}
								}}
							/>
							<ValidationError
								{...formHandler
									.for("personalSignature")
									.for("content")
									.attachToValidationError()}
							/>
						</FormItem>
					)}
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={saveHandler}
						text={localize("_Save")}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SignatureSettingsDialog.displayName = "SignatureSettingsDialog";

export function useSignatureSettingsDialog(): [
	element: ReactElement | null,
	open: () => void
] {
	const [isVisible, setIsVisible] = useState<boolean>(false);

	const render = (): ReactElement | null => isVisible
		? (
			<SignatureSettingsDialog
				onClose={() => setIsVisible(false)}
			/>
		)
		: null;

	const openHandler = useCallback(
		(): void => {
			setIsVisible(true);
		},
		[]
	);

	return [
		render(),
		openHandler
	];
}

type SignatureSettingsState = {
	signatureConstraints: SignatureConstraints | undefined;
	hasEnforcedManagedSignature: boolean;
	availableSignatures: readonly AvailableSignature[];
};

function prepareForSaving(
	data: ConfigurePersonalSignatureModel,
	hasEnforcedManagedSignature: boolean
): ConfigurePersonalSignatureModel {
	const {
		defaultSignature,
		personalSignature : {
			content,
			isEnabled
		}
	} = data;

	return {
		defaultSignature: hasEnforcedManagedSignature
			? SignatureKind.None
			: defaultSignature,
		personalSignature: {
			content: isEnabled
				? content
				: null,
			isEnabled
		}
	};
}